import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import '../styles/TeamManagement.css';

const TeamManagement = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    imageFile: null,
    imageUrl: '',
    order: 0
  });

  useEffect(() => {
    if (user && user.role === 'admin') {
      loadTeamMembers();
    }
  }, [user]);

  const loadTeamMembers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'teamMembers'));
      const members = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      members.sort((a, b) => (a.order || 0) - (b.order || 0));
      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      setFormData({ ...formData, imageFile: file });
    }
  };

  const uploadImage = async (file) => {
    const timestamp = Date.now();
    const fileName = `team/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.role) {
      alert('Please fill in all required fields');
      return;
    }

    if (!editingMember && !formData.imageFile) {
      alert('Please select an image');
      return;
    }

    try {
      setUploading(true);
      
      let imageUrl = formData.imageUrl;
      
      if (formData.imageFile) {
        imageUrl = await uploadImage(formData.imageFile);
      }

      const memberData = {
        name: formData.name,
        role: formData.role,
        imageUrl: imageUrl,
        order: formData.order || teamMembers.length,
        updatedAt: new Date()
      };

      if (editingMember) {
        await updateDoc(doc(db, 'teamMembers', editingMember.id), memberData);
        alert('Team member updated successfully!');
      } else {
        await addDoc(collection(db, 'teamMembers'), {
          ...memberData,
          createdAt: new Date()
        });
        alert('Team member added successfully!');
      }

      await loadTeamMembers();
      handleCancel();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      imageFile: null,
      imageUrl: member.imageUrl,
      order: member.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to delete ${member.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'teamMembers', member.id));
      
      // Try to delete image from storage (if it's a Firebase Storage URL)
      if (member.imageUrl && member.imageUrl.includes('firebase')) {
        try {
          const imageRef = ref(storage, member.imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.log('Could not delete image from storage:', error);
        }
      }

      await loadTeamMembers();
      alert('Team member deleted successfully!');
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Error deleting team member: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMember(null);
    setFormData({
      name: '',
      role: '',
      imageFile: null,
      imageUrl: '',
      order: 0
    });
  };

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div className="team-management">
        <div className="loading">Loading team members...</div>
      </div>
    );
  }

  return (
    <div className="team-management">
      <div className="team-header">
        <h1>Team Management</h1>
        <button onClick={() => window.location.href = '/admin'} className="btn-back">
          ← Back to Admin Panel
        </button>
      </div>

      <div className="team-actions">
        <button onClick={() => setShowForm(true)} className="btn-add-member">
          ➕ Add Team Member
        </button>
      </div>

      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <h2>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Role/Position *</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Chef, Manager, Owner"
                  required
                />
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="0"
                />
                <small>Lower numbers appear first</small>
              </div>

              <div className="form-group">
                <label>Photo {!editingMember && '*'}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!editingMember}
                />
                <small>Recommended: Square image (300x300px), under 5MB</small>
                {formData.imageFile && (
                  <div className="image-preview">
                    <img src={URL.createObjectURL(formData.imageFile)} alt="Preview" />
                  </div>
                )}
                {editingMember && !formData.imageFile && (
                  <div className="image-preview">
                    <img src={formData.imageUrl} alt="Current" />
                    <small>Current image (upload new to replace)</small>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" disabled={uploading} className="btn-save">
                  {uploading ? 'Saving...' : (editingMember ? 'Update' : 'Add')} Team Member
                </button>
                <button type="button" onClick={handleCancel} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="team-grid">
        {teamMembers.length === 0 ? (
          <div className="empty-state">
            <p>No team members yet. Add your first team member!</p>
          </div>
        ) : (
          teamMembers.map(member => (
            <div key={member.id} className="team-card">
              <div className="team-image">
                <img src={member.imageUrl} alt={member.name} />
              </div>
              <div className="team-info">
                <h3>{member.name}</h3>
                <p className="team-role">{member.role}</p>
              </div>
              <div className="team-actions-card">
                <button onClick={() => handleEdit(member)} className="btn-edit">
                  Edit
                </button>
                <button onClick={() => handleDelete(member)} className="btn-delete">
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
